import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Breadcrumb, Typography, Card, Table, Button, Space, Modal, Form, Input, Select,
  message, Popconfirm, Spin, Tag, Row, Col,
} from 'antd';
import {
  ArrowUpOutlined, ArrowDownOutlined, PlusOutlined, DeleteOutlined,
  SaveOutlined, ApartmentOutlined,
} from '@ant-design/icons';
import { workflowApi } from '../../services/api';

const { Title } = Typography;

const TYPE_OPTIONS = [
  { value: 'approval', label: '审批' },
  { value: 'auto', label: '自动' },
  { value: 'manual', label: '手动' },
];

const ROLE_OPTIONS = [
  { value: 'admin', label: '管理员' },
  { value: 'manager', label: '主管' },
  { value: 'sales', label: '销售' },
];

const NOTIFY_OPTIONS = [
  { value: 'wecom', label: '企业微信' },
  { value: 'email', label: '邮件' },
  { value: 'system', label: '站内信' },
];

export default function TemplateEdit() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [form] = Form.useForm();

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const res = await workflowApi.getTemplate(code);
      setTemplate(res);
      const parsed = (() => { try { return JSON.parse(res.stages); } catch { return []; } })();
      setStages(parsed);
    } catch (e) {
      message.error(e?.error || '加载模板失败');
      navigate('/workflow');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplate(); }, [code]);

  const moveStage = (index, direction) => {
    const newStages = [...stages];
    const target = index + direction;
    if (target < 0 || target >= newStages.length) return;
    [newStages[index], newStages[target]] = [newStages[target], newStages[index]];
    // 重新计算 order
    const reordered = newStages.map((s, i) => ({ ...s, order: i + 1 }));
    setStages(reordered);
  };

  const openAddModal = () => {
    setEditingStage(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (stage) => {
    setEditingStage(stage);
    form.setFieldsValue({
      name: stage.name,
      type: stage.type,
      role: stage.role,
      action: stage.action,
      notify: stage.notify ? stage.notify.split(',').map(s => s.trim()) : [],
    });
    setModalOpen(true);
  };

  const handleSaveStage = async () => {
    try {
      const values = await form.validateFields();
      const stageData = {
        ...values,
        notify: values.notify ? values.notify.join(',') : undefined,
      };

      let newStages;
      if (editingStage) {
        // 编辑
        newStages = stages.map(s =>
          s.order === editingStage.order ? { ...s, ...stageData } : s
        );
      } else {
        // 新增
        const maxOrder = stages.reduce((max, s) => Math.max(max, s.order), 0);
        newStages = [...stages, { ...stageData, order: maxOrder + 1 }];
      }
      setStages(newStages);
      setModalOpen(false);
    } catch (e) {
      // validation failed
    }
  };

  const deleteStage = (order) => {
    const filtered = stages.filter(s => s.order !== order);
    const reordered = filtered.map((s, i) => ({ ...s, order: i + 1 }));
    setStages(reordered);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await workflowApi.updateTemplate(code, {
        stages: JSON.stringify(stages),
      });
      message.success('保存成功');
      fetchTemplate();
    } catch (e) {
      message.error(e?.error || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: '顺序',
      dataIndex: 'order',
      width: 60,
      render: (val) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: '阶段名称',
      dataIndex: 'name',
    },
    {
      title: '类型',
      dataIndex: 'type',
      width: 100,
      render: (val) => {
        const colors = { approval: 'blue', auto: 'green', manual: 'orange' };
        const labels = { approval: '审批', auto: '自动', manual: '手动' };
        return <Tag color={colors[val] || 'default'}>{labels[val] || val}</Tag>;
      },
    },
    {
      title: '审批角色',
      dataIndex: 'role',
      width: 100,
      render: (val) => {
        const labels = { admin: '管理员', manager: '主管', sales: '销售' };
        return val ? <Tag>{labels[val] || val}</Tag> : <span style={{ color: '#ccc' }}>—</span>;
      },
    },
    {
      title: '自动操作',
      dataIndex: 'action',
      width: 120,
      render: (val) => val || <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      title: '通知方式',
      dataIndex: 'notify',
      width: 150,
      render: (val) => {
        if (!val) return <span style={{ color: '#ccc' }}>—</span>;
        const channels = val.split(',').map(s => s.trim());
        return (
          <Space size={4}>
            {channels.map((ch) => {
              const colors = { wecom: 'green', email: 'orange', system: 'blue' };
              const labels = { wecom: '企微', email: '邮件', system: '站内信' };
              return <Tag key={ch} color={colors[ch] || 'default'}>{labels[ch] || ch}</Tag>;
            })}
          </Space>
        );
      },
    },
    {
      title: '操作',
      width: 200,
      render: (_, record, index) => (
        <Space>
          <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0}
            onClick={() => moveStage(index, -1)} />
          <Button size="small" icon={<ArrowDownOutlined />} disabled={index === stages.length - 1}
            onClick={() => moveStage(index, 1)} />
          <Button size="small" type="link" onClick={() => openEditModal(record)}>编辑</Button>
          <Popconfirm title="确定删除此阶段？" onConfirm={() => deleteStage(record.order)}>
            <Button size="small" type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) return <Spin tip="加载中..." style={{ display: 'block', textAlign: 'center', padding: 80 }} />;

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <a onClick={() => navigate('/workflow')}>工作流配置</a> },
          { title: template?.name || code },
        ]}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <ApartmentOutlined style={{ marginRight: 8 }} />
            {template?.name || code}
          </Title>
          <Tag color="blue" style={{ marginTop: 8 }}>{template?.code}</Tag>
          <Tag>{template?.module}</Tag>
        </div>
        <Space>
          <Button onClick={() => navigate('/workflow')}>返回列表</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            保存
          </Button>
        </Space>
      </div>

      <Card
        title={`阶段配置（共 ${stages.length} 个阶段）`}
        extra={<Button type="dashed" icon={<PlusOutlined />} onClick={openAddModal}>新增阶段</Button>}
      >
        <Table
          dataSource={stages}
          columns={columns}
          rowKey="order"
          pagination={false}
          size="middle"
        />
      </Card>

      <Modal
        title={editingStage ? '编辑阶段' : '新增阶段'}
        open={modalOpen}
        onOk={handleSaveStage}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="阶段名称" rules={[{ required: true, message: '请输入阶段名称' }]}>
            <Input placeholder="如：销售主管审批" />
          </Form.Item>
          <Form.Item name="type" label="阶段类型" rules={[{ required: true, message: '请选择类型' }]}>
            <Select options={TYPE_OPTIONS} placeholder="选择类型" />
          </Form.Item>
          <Form.Item name="role" label="审批角色">
            <Select options={ROLE_OPTIONS} placeholder="选择角色（选填）" allowClear />
          </Form.Item>
          <Form.Item name="action" label="自动操作">
            <Select
              placeholder="选择自动操作（选填）"
              allowClear
              options={[
                { value: 'signContract', label: '签署合同' },
                { value: 'confirmQuotation', label: '确认报价' },
                { value: 'adjustStock', label: '调整库存' },
              ]}
            />
          </Form.Item>
          <Form.Item name="notify" label="通知方式">
            <Select mode="multiple" options={NOTIFY_OPTIONS} placeholder="选择通知方式（选填）" allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
